import moment from "moment";
import { AsyncSubject, Observable } from "rxjs";

export class IndexedDBStorageClass {
    private static _instance = null;

    constructor() {
        if (IndexedDBStorageClass._instance) {
            throw new Error("Error: Instantiation failed: Use IndexedDBStorageClass.getInstance() instead of new.");
        }

        return IndexedDBStorageClass._instance = this;
    }

    public static getInstance(): IndexedDBStorageClass {
        return IndexedDBStorageClass._instance;
    }

    public createDatabase(dbName: string): Observable<string> {
        const dbconnect = window.indexedDB.open(dbName, 1);
        const subject: AsyncSubject<string> = new AsyncSubject();

        dbconnect.onupgradeneeded = (ev: any) => {
            const db: IDBDatabase = ev.target.result;

            if (!db.objectStoreNames.contains('Notifications')) {
                const store: IDBObjectStore = db.createObjectStore('Notifications', { keyPath: 'id', autoIncrement: true });

                store.createIndex('notification', 'notification', { unique: false });
                store.createIndex('data', 'data', { unique: false });
                store.createIndex('priority', 'priority', { unique: false });
                store.createIndex('collapse_key', 'collapse_key', { unique: false });
                store.createIndex('id', 'id', { unique: true });
            }
        }

        dbconnect.onsuccess = (ev: any) => {
            subject.next('COMPLETE');
            subject.complete();
        }

        dbconnect.onerror = (evt: any) => {
            subject.error(evt.target.error.message);
        }

        return subject;
    }

    public getDatabase<T>(dbName: string): Observable<T[]> {
        const dbconnect: IDBOpenDBRequest = window.indexedDB.open(dbName, 1);
        const notifications = [];
        const subject: AsyncSubject<T[]> = new AsyncSubject();

        dbconnect.onsuccess = (ev: any) => {
            const db: IDBDatabase = ev.target.result;

            if (db.objectStoreNames.contains('Notifications')) {
                const store = db.transaction('Notifications', 'readonly').objectStore('Notifications');
                const query = store.openCursor();

                query.onsuccess = (ev: any) => {
                    const cursor: IDBCursorWithValue = ev.target.result;

                    if (cursor) {
                        const isExpired: boolean = this.isNotificationExpired(cursor);

                        if (isExpired) {
                            this.deleteDatabaseItem(dbName, cursor.key);
                        } else {
                            notifications.push(cursor.value);
                        }

                        cursor.continue();
                    } else {
                        subject.next(notifications);
                        subject.complete();
                    }
                };
            }
        }

        dbconnect.onerror = (evt: any) => {
            subject.error(evt.target.error.message);
        };

        return subject;
    }

    private isNotificationExpired(notification: IDBCursorWithValue): boolean {
        const creationDate: Date = new Date(parseFloat(notification.key.toString()));
        const notificationExp = moment(creationDate).add(48, 'hours');

        if (moment(new Date()).isAfter(notificationExp)) {
            return true;
        }

        return false;
    }

    public deleteDatabaseItem(dbName: string, key: IDBValidKey): Observable<string> {
        const dbconnect: IDBOpenDBRequest = window.indexedDB.open(dbName, 1);
        const subject: AsyncSubject<string> = new AsyncSubject();

        dbconnect.onsuccess = (ev: any) => {
            const db: IDBDatabase = ev.target.result;

            const store = db.transaction('Notifications', "readwrite").objectStore('Notifications').delete(key);

            store.onsuccess = (ev: any) => {
                subject.next('COMPLETE');
                subject.complete();
            }

            store.onerror = (evt: any) => {
                subject.error(evt.target.error.message);
            }
        }

        return subject;
    }

    public writeInDatabase(objectStore: any, dbname: string): Observable<string> {
        const dbconnect: IDBOpenDBRequest = window.indexedDB.open(dbname, 1);
        const subject: AsyncSubject<string> = new AsyncSubject();

        dbconnect.onsuccess = (ev: any) => {
            const db: IDBDatabase = ev.target.result;

            const transaction: IDBTransaction = db.transaction('Notifications', 'readwrite');
            const store: IDBObjectStore = transaction.objectStore('Notifications');

            const str = store.put(objectStore);

            str.onsuccess = (ev: any) => {
                subject.next('COMPLETE');
                subject.complete();
            }

            str.onerror = (evt: any) => {
                subject.error(evt.target.error.message);
            }
        }

        return subject;
    }
}
